import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadState, LoadStateInfo, loaded, loading, notStarted, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { ScheduleInfo } from "./model";

export interface ScheduleListState {
    state: LoadStateInfo;
    request?: RetrieveScheduleListRequest;
    list?: ScheduleInfo[];
}

const initialState: ScheduleListState = {
    state: notStarted(),
};

export interface RetrieveScheduleListRequest {
    schoolId: string;
    classroomId?: string;
    subjectId?: string;
}

export const retrieveScheduleList = createAsyncThunk(
    "retrieveScheduleList",
    async (param: RetrieveScheduleListRequest, thunkAPI) => {
        console.debug(`retrieve schedules for school ${param.schoolId}`);

        let url = `${getBackendUrl()}/api/schedule/school/${param.schoolId}`;
        if (param.classroomId) {
            url = `${getBackendUrl()}/api/schedule/classroom/${param.classroomId}`;
        } else if (param.subjectId) {
            url = `${getBackendUrl()}/api/schedule/subject/${param.subjectId}`;
        }
        const response = await fetch(url, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (_param: RetrieveScheduleListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = (state as any).scheduleList?.state as LoadStateInfo | undefined;
            if (status && (status.state == LoadState.Loading || status.state == LoadState.Failed)) {
                return false;
            }
            return true;
        }
    }
);

export const scheduleListSlice = createSlice({
    name: "scheduleList",
    initialState,
    reducers: {
        check: (state, action: PayloadAction<RetrieveScheduleListRequest>) => {
            if (`${state.request?.schoolId}` != `${action.payload.schoolId}`
                || `${state.request?.classroomId}` != `${action.payload.classroomId}`
                || `${state.request?.subjectId}` != `${action.payload.subjectId}`
            ) {
                state.state = notStarted();
                state.request = { schoolId: action.payload.schoolId, classroomId: action.payload.classroomId, subjectId: action.payload.subjectId };
                state.list = undefined;
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveScheduleList.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveScheduleList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                state.list?.sort((a, b) => {
                    const dayCompare = (a?.dayOfWeek || 0) - (b?.dayOfWeek || 0);
                    if (dayCompare !== 0) return dayCompare;
                    return (a?.startTime || "").localeCompare(b?.startTime || "");
                });
            })
            .addCase(retrieveScheduleList.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export const selectScheduleList = (state: RootState) => (state as any).scheduleList as ScheduleListState;

export default scheduleListSlice.reducer;
